import dayjs from "dayjs";
import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";

import tzlookup from "tz-lookup";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { addOpenMeteoForecastRequests } from "../../../utils/openMeteoForecast.js";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

// -----------------------------
// Utilities
// -----------------------------
function sortTemps(temps) {
  return (temps || [])
    .slice()
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

function getTimezone(latLong) {
  if (!latLong) return null;
  const [lat, lon] = latLong.split(",").map(Number);
  return tzlookup(lat, lon);
}

function getTodayDate(latLong) {
  if (!latLong) return dayjs();
  const tz = getTimezone(latLong);
  return tz ? dayjs().tz(tz) : dayjs();
}

// -----------------------------
// Temperature fetchers
// -----------------------------
async function fetchFromOpenMeteo(latLong, startDate, endDate, siteMetadata) {
  const all = [];

  // Validate lat/long
  const [latStr, lonStr] = String(latLong)
    .split(",")
    .map((s) => s.trim());
  const lat = parseFloat(latStr);
  const lon = parseFloat(lonStr);
  if (
    Number.isNaN(lat) ||
    Number.isNaN(lon) ||
    lat < -90 ||
    lat > 90 ||
    lon < -180 ||
    lon > 180
  ) {
    throw new Error('Invalid latLong. Expected "lat,lon".');
  }

  // Validate dates
  const inStart = dayjs(startDate, BACKEND_DATE_FORMAT, true);
  const inEnd = dayjs(endDate, BACKEND_DATE_FORMAT, true);
  if (!inStart.isValid() || !inEnd.isValid())
    throw new Error(`Invalid date format. Use ${BACKEND_DATE_FORMAT}`);
  if (inEnd.isBefore(inStart))
    throw new Error("endDate cannot be earlier than startDate.");

  const today = getTodayDate(latLong);
  const historicalStartMin = dayjs("1940-01-01");
  const historicalEndMax = today.subtract(3, "day"); // last day archive is available
  const forecastStartMin = historicalEndMax.add(1, "day");
  const forecastEndMax = today.add(14, "day");

  // Intersections
  const histStart = inStart.isBefore(historicalStartMin)
    ? historicalStartMin
    : inStart;
  const histEnd = inEnd.isBefore(historicalEndMax) ? inEnd : historicalEndMax;

  const foreStart = inStart.isAfter(forecastStartMin)
    ? inStart
    : forecastStartMin;
  const foreEnd = inEnd.isAfter(forecastEndMax) ? forecastEndMax : inEnd;

  const timezoneName = getTimezone(latLong);
  if (!timezoneName) throw new Error("Failed to resolve timezone for latLong");

  const requests = [];

  // Historical slice
  if (!histStart.isAfter(histEnd)) {
    const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${histStart.format(BACKEND_DATE_FORMAT)}&end_date=${histEnd.format(BACKEND_DATE_FORMAT)}&daily=temperature_2m_max,temperature_2m_min&timezone=${encodeURIComponent(timezoneName)}`;
    requests.push(
      fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error(`Archive API ${r.status}`);
          return r.json();
        })
        .then((data) => {
          if (!data?.daily?.time) return [];
          return data.daily.time.map((d, i) => ({
            date: dayjs(d).format(BACKEND_DATE_FORMAT),
            max: data.daily.temperature_2m_max[i],
            min: data.daily.temperature_2m_min[i],
          }));
        }),
    );
  }

  // Forecast slice
  if (!foreStart.isAfter(foreEnd)) {
    addOpenMeteoForecastRequests({
      fetchPromises: requests,
      lat,
      lon,
      forecastStart: foreStart,
      forecastEnd: foreEnd,
      timezone: timezoneName,
      siteMetadata,
      dateFormat: BACKEND_DATE_FORMAT,
    });
  }

  if (requests.length === 0) return all;

  const results = await Promise.allSettled(requests);
  for (const res of results) {
    if (res.status === "fulfilled") all.push(...res.value);
    else console.error("Temperature fetch error:", res.reason);
  }
  return all;
}

async function fetchTemperatureData(latLong, startDate, endDate, siteMetadata) {
  if (!latLong) return [];
  try {
    const out = await fetchFromOpenMeteo(
      latLong,
      startDate,
      endDate,
      siteMetadata,
    );
    return Array.isArray(out) ? out : [];
  } catch (e) {
    console.error("fetchTemperatureData error:", e);
    return [];
  }
}

// -----------------------------
// Public service
// -----------------------------
export const forecastService = {
  /**
   * Returns:
   * {
   *   temps: Array<{ date:'YYYY-MM-DD', max:number, min:number }>,
   *   meta: { latLong, selectedISO, startISO, endISO, todayISO, historyYears, horizonDays }
   * }
   */
  async getForecast({
    latLong,
    selectedISO, // 'YYYY-MM-DD'
    endISO, // 'YYYY-MM-DD' (planner's visible end)
    historyYears,
    horizonDays,
    startOverrideISO,
    endOverrideISO,
    siteMetadata,
  }) {
    if (!selectedISO || !endISO) {
      throw new Error("selectedISO and endISO are required");
    }

    const startISO = startOverrideISO
      ? dayjs(startOverrideISO).format(BACKEND_DATE_FORMAT)
      : dayjs(selectedISO)
          .subtract(historyYears, "year")
          .format(BACKEND_DATE_FORMAT);

    const finalEndISO = endOverrideISO
      ? dayjs(endOverrideISO).format(BACKEND_DATE_FORMAT)
      : dayjs(endISO).add(horizonDays, "day").format(BACKEND_DATE_FORMAT);

    const temps = sortTemps(
      await fetchTemperatureData(latLong, startISO, finalEndISO, siteMetadata),
    );

    return {
      temps,
      meta: {
        latLong,
        selectedISO,
        startISO,
        endISO: finalEndISO,
        todayISO: getTodayDate(latLong).format(BACKEND_DATE_FORMAT),
        historyYears,
        horizonDays,
        override: !!(startOverrideISO && endOverrideISO),
      },
    };
  },
};
